/**
 * Read and write a node's config at a field's dot path.
 *
 * A `FieldSpec.path` is a dot path into `config` (`component-meta.ts`), and
 * meta validation already walks it (`schemaAt`). The builder's compile, seed
 * and publish check read it as one flat key, so a line under a nested object —
 * `CallerIntent`'s `frontDesk.anythingElseSlot` — was never collected, renamed
 * or checked: the builder's copy of the inbound workflow had no words for the
 * front desk's two lines, and a test call of it went silent where they belong.
 * These two are the one way those three places reach a field.
 */

/** The value at `path`, or undefined when any segment is missing. */
export function configAt(config: Record<string, unknown>, path: string): unknown {
  let at: unknown = config;
  for (const segment of path.split('.')) {
    if (typeof at !== 'object' || at === null || Array.isArray(at)) return undefined;
    at = (at as Record<string, unknown>)[segment];
  }
  return at;
}

/**
 * `config` with `value` at `path`, copying each object on the way down so the
 * caller's config is never mutated. `undefined` REMOVES the field, which is
 * how the builder clears one. A missing parent is created; a parent that is
 * not an object is left alone, since that is a malformed config the schema
 * reports on its own.
 */
export function withConfigAt(
  config: Record<string, unknown>,
  path: string,
  value: unknown
): Record<string, unknown> {
  const [head, ...rest] = path.split('.');
  if (head === undefined) return config;
  if (rest.length === 0) {
    if (value !== undefined) return { ...config, [head]: value };
    const { [head]: _removed, ...kept } = config;
    return kept;
  }
  const inner = config[head];
  if (
    inner !== undefined &&
    (typeof inner !== 'object' || inner === null || Array.isArray(inner))
  ) {
    return config;
  }
  return {
    ...config,
    [head]: withConfigAt((inner as Record<string, unknown>) ?? {}, rest.join('.'), value),
  };
}
